import React, { useMemo, useState } from 'react';
import { Badge, Button, Card, Spinner, Table } from 'react-bootstrap';
import {
  useDeleteProjectMutation,
  useDeleteTaskMutation,
  useGetProjectsQuery,
  useGetTasksQuery,
  useUpdateProjectMutation,
  useUpdateTaskMutation,
} from '../../services/api';
import ERMSectionHeader from './ERMSectionHeader';

interface ErmProject {
  id: string;
  title?: string;
  stage?: string;
  archived?: boolean;
  archivedAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

interface ErmTask {
  id: string;
  subject?: string;
  projectId?: string;
  phase?: string;
  archived?: boolean;
  archivedAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

function formatArchiveDate(value: unknown): string {
  const raw = String(value || '').trim();
  if (!raw) return '—';
  const parsed = Date.parse(raw);
  if (!Number.isFinite(parsed)) return raw;
  return new Date(parsed).toLocaleString();
}

export default function ERMArchive() {
  const [search, setSearch] = useState('');
  const taskQueryArg = useMemo(() => ({ scope: 'visible', assignee: 'all', archived: true }), []);

  const { data: archivedProjectsRaw = [], isLoading: isLoadingArchivedProjects, refetch: refetchArchivedProjects } = useGetProjectsQuery({ archived: true });
  const { data: archivedTasksRaw = [], isLoading: isLoadingArchivedTasks, refetch: refetchArchivedTasks } = useGetTasksQuery(taskQueryArg);
  const { data: allProjectsRaw = [], isLoading: isLoadingAllProjects } = useGetProjectsQuery({ archived: 'all' });

  const [updateProject, { isLoading: isUpdatingProject }] = useUpdateProjectMutation();
  const [updateTask, { isLoading: isUpdatingTask }] = useUpdateTaskMutation();
  const [deleteProject, { isLoading: isDeletingProject }] = useDeleteProjectMutation();
  const [deleteTask, { isLoading: isDeletingTask }] = useDeleteTaskMutation();

  const archivedProjects = useMemo<ErmProject[]>(
    () => (Array.isArray(archivedProjectsRaw) ? (archivedProjectsRaw as ErmProject[]) : []),
    [archivedProjectsRaw]
  );
  const archivedTasks = useMemo<ErmTask[]>(
    () => (Array.isArray(archivedTasksRaw) ? (archivedTasksRaw as ErmTask[]) : []),
    [archivedTasksRaw]
  );
  const allProjects = useMemo<ErmProject[]>(
    () => (Array.isArray(allProjectsRaw) ? (allProjectsRaw as ErmProject[]) : []),
    [allProjectsRaw]
  );

  const projectTitleById = useMemo(() => {
    const map = new Map<string, string>();
    allProjects.forEach((project) => {
      const id = String(project?.id || '').trim();
      if (!id) return;
      const title = String(project?.title || '').trim() || id;
      map.set(id, title);
    });
    return map;
  }, [allProjects]);

  const normalizedSearch = search.trim().toLowerCase();

  const filteredProjects = useMemo(() => {
    const list = [...archivedProjects];
    list.sort((left, right) => {
      const leftSortValue = Date.parse(String(left.archivedAt || left.updatedAt || '')) || 0;
      const rightSortValue = Date.parse(String(right.archivedAt || right.updatedAt || '')) || 0;
      return rightSortValue - leftSortValue;
    });
    if (!normalizedSearch) return list;
    return list.filter((project) => {
      const title = String(project?.title || '').toLowerCase();
      const stage = String(project?.stage || '').toLowerCase();
      return title.includes(normalizedSearch) || stage.includes(normalizedSearch);
    });
  }, [archivedProjects, normalizedSearch]);

  const filteredTasks = useMemo(() => {
    const list = [...archivedTasks];
    list.sort((left, right) => {
      const leftSortValue = Date.parse(String(left.archivedAt || left.updatedAt || '')) || 0;
      const rightSortValue = Date.parse(String(right.archivedAt || right.updatedAt || '')) || 0;
      return rightSortValue - leftSortValue;
    });
    if (!normalizedSearch) return list;
    return list.filter((task) => {
      const subject = String(task?.subject || '').toLowerCase();
      const phase = String(task?.phase || '').toLowerCase();
      const projectName = String(projectTitleById.get(String(task?.projectId || '').trim()) || task?.projectId || '').toLowerCase();
      return subject.includes(normalizedSearch) || phase.includes(normalizedSearch) || projectName.includes(normalizedSearch);
    });
  }, [archivedTasks, normalizedSearch, projectTitleById]);

  const handleRestoreProject = async (projectId: string) => {
    try {
      await updateProject({ projectId, archived: false }).unwrap();
      await Promise.all([refetchArchivedProjects(), refetchArchivedTasks()]);
    } catch (error) {
      console.error('Failed to restore project:', error);
    }
  };

  const handleRestoreTask = async (taskId: string) => {
    try {
      await updateTask({ taskId, archived: false }).unwrap();
      await refetchArchivedTasks();
    } catch (error) {
      console.error('Failed to restore task:', error);
    }
  };

  const handlePermanentDeleteProject = async (projectId: string, projectTitle: string) => {
    const confirmed = window.confirm(`Permanently delete "${projectTitle}"? This cannot be undone.`);
    if (!confirmed) return;
    try {
      await deleteProject(projectId).unwrap();
      await Promise.all([refetchArchivedProjects(), refetchArchivedTasks()]);
    } catch (error) {
      console.error('Failed to permanently delete project:', error);
    }
  };

  const handlePermanentDeleteTask = async (taskId: string, taskSubject: string) => {
    const confirmed = window.confirm(`Permanently delete "${taskSubject}"? This cannot be undone.`);
    if (!confirmed) return;
    try {
      await deleteTask(taskId).unwrap();
      await refetchArchivedTasks();
    } catch (error) {
      console.error('Failed to permanently delete task:', error);
    }
  };

  const isLoading = isLoadingArchivedProjects || isLoadingArchivedTasks || isLoadingAllProjects;

  return (
    <div className="erm-archive">
      <ERMSectionHeader
        title="Archive"
        backTo="/erm/projects"
        showHelp
        searchPlaceholder="Search archived projects and tasks"
        searchValue={search}
        onSearchChange={setSearch}
      />

      <div className="mt-3 d-flex flex-wrap align-items-center gap-2">
        <Badge bg="secondary">{archivedProjects.length} project{archivedProjects.length === 1 ? '' : 's'}</Badge>
        <Badge bg="secondary">{archivedTasks.length} task{archivedTasks.length === 1 ? '' : 's'}</Badge>
        <span className="text-muted" style={{ fontSize: '0.78rem' }}>
          Permanently deleting removes data forever.
        </span>
      </div>

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <div className="d-flex flex-column gap-3 mt-3">
          <Card className="border">
            <Card.Header className="d-flex justify-content-between align-items-center py-2">
              <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>Archived projects</span>
              <span className="text-muted" style={{ fontSize: '0.78rem' }}>
                {filteredProjects.length} result{filteredProjects.length === 1 ? '' : 's'}
              </span>
            </Card.Header>
            <div className="table-responsive">
              <Table hover size="sm" className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ fontSize: '0.75rem' }}>Project</th>
                    <th style={{ width: '180px', fontSize: '0.75rem' }}>Stage</th>
                    <th style={{ width: '240px', fontSize: '0.75rem' }}>Archived</th>
                    <th style={{ width: '260px', fontSize: '0.75rem' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-4 text-muted">
                        No archived projects found.
                      </td>
                    </tr>
                  ) : (
                    filteredProjects.map((project) => {
                      const projectId = String(project?.id || '').trim();
                      const projectTitle = String(project?.title || '').trim() || 'Untitled project';
                      return (
                        <tr key={projectId}>
                          <td style={{ fontSize: '0.82rem' }}>{projectTitle}</td>
                          <td style={{ fontSize: '0.8rem' }}>{String(project?.stage || '').trim() || '—'}</td>
                          <td style={{ fontSize: '0.8rem' }}>{formatArchiveDate(project?.archivedAt || project?.updatedAt)}</td>
                          <td>
                            <div className="d-flex flex-wrap gap-2">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                style={{ fontSize: '0.72rem' }}
                                onClick={() => handleRestoreProject(projectId)}
                                disabled={isUpdatingProject || isDeletingProject}
                              >
                                Restore
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                style={{ fontSize: '0.72rem' }}
                                onClick={() => handlePermanentDeleteProject(projectId, projectTitle)}
                                disabled={isDeletingProject || isUpdatingProject}
                              >
                                Permanently delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          </Card>

          <Card className="border">
            <Card.Header className="d-flex justify-content-between align-items-center py-2">
              <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>Archived tasks</span>
              <span className="text-muted" style={{ fontSize: '0.78rem' }}>
                {filteredTasks.length} result{filteredTasks.length === 1 ? '' : 's'}
              </span>
            </Card.Header>
            <div className="table-responsive">
              <Table hover size="sm" className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ fontSize: '0.75rem' }}>Subject</th>
                    <th style={{ width: '220px', fontSize: '0.75rem' }}>Project</th>
                    <th style={{ width: '170px', fontSize: '0.75rem' }}>Phase</th>
                    <th style={{ width: '240px', fontSize: '0.75rem' }}>Archived</th>
                    <th style={{ width: '260px', fontSize: '0.75rem' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTasks.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-4 text-muted">
                        No archived tasks found.
                      </td>
                    </tr>
                  ) : (
                    filteredTasks.map((task) => {
                      const taskId = String(task?.id || '').trim();
                      const taskSubject = String(task?.subject || '').trim() || 'Untitled task';
                      const projectId = String(task?.projectId || '').trim();
                      const projectName = projectTitleById.get(projectId) || projectId || '—';
                      return (
                        <tr key={taskId}>
                          <td style={{ fontSize: '0.82rem' }}>{taskSubject}</td>
                          <td style={{ fontSize: '0.8rem' }}>{projectName}</td>
                          <td style={{ fontSize: '0.8rem' }}>{String(task?.phase || '').trim() || '—'}</td>
                          <td style={{ fontSize: '0.8rem' }}>{formatArchiveDate(task?.archivedAt || task?.updatedAt)}</td>
                          <td>
                            <div className="d-flex flex-wrap gap-2">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                style={{ fontSize: '0.72rem' }}
                                onClick={() => handleRestoreTask(taskId)}
                                disabled={isUpdatingTask || isDeletingTask}
                              >
                                Restore
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                style={{ fontSize: '0.72rem' }}
                                onClick={() => handlePermanentDeleteTask(taskId, taskSubject)}
                                disabled={isDeletingTask || isUpdatingTask}
                              >
                                Permanently delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
